import { searchTasks, TaskSearchQuery } from "../taskServices/TaskSearchService";
import { getAllParticipants } from "../participantServices/ParticipantService";
import { getAllUsersInOrganizationbyId } from "../userServices/UserInfoService";
import TaskList from "../../interfaces/task/TaskList";
import AssignedParticipant from "../../interfaces/participant/AssignedParticipant";
import UserInfo from "../../interfaces/UserInfo";
import { searchParticipants } from "./ParticipantSearchService";

export type SearchResult =
    | { type: 'task'; id: string; friendlyId: string; title: string; participantName: string }
    | { type: 'participant'; id: string; firstName: string; lastName: string; region: string; waiver: string }
    | { type: 'employee'; id: string; firstName: string; lastName: string; email: string };

let cachedEmployees: UserInfo[] | null = null;
let cachedEmployeesAuthToken: string | null = null;

async function getCachedEmployees(authToken: string): Promise<UserInfo[] | null> {
  // naive per-session cache; also keep it token-scoped so switching users doesn't leak data
  if (cachedEmployees && cachedEmployeesAuthToken === authToken) {
    return cachedEmployees;
  }

  const employees = await getAllUsersInOrganizationbyId(authToken);
  cachedEmployees = employees;
  cachedEmployeesAuthToken = authToken;
  return employees;
}

export async function globalSearch(authToken: string, searchTerm: string): Promise<SearchResult[]> {
  const taskQuery: TaskSearchQuery = { searchTerm, pageSize: 5 };

  try {
    const [taskResults, participantSearch, employeeResults] = await Promise.all([
      searchTasks(authToken, taskQuery),
      // old: getAllParticipants(authToken)
      searchParticipants(authToken, { searchTerm, pageSize: 5 }),
      getCachedEmployees(authToken)
    ]);

    const tasks: SearchResult[] =
      taskResults?.tasks?.map((task: TaskList) => ({
        type: "task",
        id: task.id,
        friendlyId: task.friendlyId,
        title: task.title || "Untitled Task",
        participantName: `${task.participant?.firstName ?? ""} ${task.participant?.lastName ? task.participant.lastName.charAt(0) + "." : ""}`.trim()
      })) ?? [];

    const participants: SearchResult[] =
      participantSearch?.participants?.map(p => ({
        type: "participant",
        id: String(p.id),
        firstName: p.firstName,
        lastName: p.lastName,
        region: String(p.regionId),
        waiver: p.waiverName ?? ""
      })) ?? [];

    const employees: SearchResult[] =
      employeeResults
        ?.filter(e => {
          const s = searchTerm.toLowerCase();
          const name = `${e.firstName ?? ""} ${e.lastName ?? ""}`.trim().toLowerCase();
          const email = (e.email ?? "").toLowerCase();
          const displayName = (e.displayName ?? "").toLowerCase();
          return name.includes(s) || displayName.includes(s) || email.includes(s);
        })
        .map((e: UserInfo) => ({
          type: "employee",
          id: e.id,
          firstName: e.firstName,
          lastName: e.lastName,
          email: e.email
        })) ?? [];

    // order however you prefer
    return [...employees, ...participants, ...tasks];
  } catch (error) {
    console.error("Error in global search:", error);
    return [];
  }
}

